import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ShoppingCartOutlined,
  PersonOutline,
  Menu as MenuIcon,
  Close as CloseIcon,
  HomeOutlined,
  StorefrontOutlined,
  PrecisionManufacturingOutlined,
  DesignServicesOutlined,
  InfoOutlined,
  HelpOutlineOutlined,
  AlternateEmailOutlined,
  CalculateOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  AccountCircleOutlined,
  AdminPanelSettingsOutlined,
  LogoutOutlined,
  LoginOutlined,
  PersonAddOutlined,
  KeyboardArrowDown,
  TuneOutlined,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { COLORS, withOpacity } from '../../theme/colors';

const Navbar = () => {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [servicesMenuAnchor, setServicesMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user, userData, logout, isAdmin } = useAuth();
  const { getCartItemCount } = useCart();
  const cartCount = getCartItemCount();

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleServicesMenuOpen = (event) => {
    setServicesMenuAnchor(event.currentTarget);
  };

  const handleServicesMenuClose = () => {
    setServicesMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleUserMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Products', path: '/products' },
    { label: 'Services', path: null, hasDropdown: true },
    { label: 'About', path: '/about' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  const servicesMenuItems = [
    {
      label: '3D Printing Service',
      description: 'Upload STL/CAD & get instant quote',
      path: '/services/3d-printing',
      icon: <PrecisionManufacturingOutlined fontSize="small" sx={{ color: COLORS.primary.main }} />,
    },
    {
      label: '3D Design & Prototyping',
      description: 'Turn ideas & sketches into 3D CAD models',
      path: '/services/3d-design-prototyping',
      icon: <DesignServicesOutlined fontSize="small" sx={{ color: COLORS.accent.coral }} />,
    },
  ];

  const isServicesActive = location.pathname.startsWith('/services');

  // Mobile Drawer Component (Modern M3 side sheet)
  const MobileDrawer = () => (
    <Drawer
      anchor="right"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '84vw', sm: 340 },
          maxWidth: 360,
          bgcolor: COLORS.surface.containerLowest,
          color: COLORS.text.primary,
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
        },
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
          bgcolor: COLORS.surface.containerLow,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.25} onClick={() => { navigate('/'); setMobileDrawerOpen(false); }} sx={{ cursor: 'pointer' }}>
          <img
            src="/images/The-Random-Store---Logo---Transparent - Copy.png"
            alt="The Random Store Logo"
            style={{ width: 34, height: 34, objectFit: 'contain' }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1, fontFamily: '"Fredoka", "Inter", sans-serif' }}>
              The Random Store
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.text.secondary, fontSize: '0.7rem', fontWeight: 600 }}>
              3D Print Lab &amp; Custom Store
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={() => setMobileDrawerOpen(false)}
          size="small"
          aria-label="Close menu"
          sx={{
            color: COLORS.text.secondary,
            bgcolor: COLORS.surface.container,
            '&:hover': { bgcolor: COLORS.surface.containerHigh },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Quick 3D Quote CTA Banner in Drawer */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<CalculateOutlined />}
          onClick={() => {
            navigate('/services/3d-printing');
            setMobileDrawerOpen(false);
          }}
          sx={{
            py: 1.25,
            fontWeight: 700,
            fontSize: '0.88rem',
            letterSpacing: '0.02em',
            boxShadow: '0 4px 14px rgba(74, 191, 24, 0.28)',
          }}
        >
          Instant 3D Print Quote
        </Button>
      </Box>

      {/* Navigation List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 1 }}>
        <List disablePadding>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/'}
              onClick={() => { navigate('/'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.text.secondary }}>
                <HomeOutlined />
              </ListItemIcon>
              <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/products'}
              onClick={() => { navigate('/products'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.text.secondary }}>
                <StorefrontOutlined />
              </ListItemIcon>
              <ListItemText primary="Explore Products" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }} />
            </ListItemButton>
          </ListItem>

          {/* Services Section */}
          <Box sx={{ my: 1.5, px: 1 }}>
            <Typography variant="caption" sx={{ color: COLORS.text.secondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}>
              Maker Services
            </Typography>
          </Box>

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/services/3d-printing'}
              onClick={() => { navigate('/services/3d-printing'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.primary.main }}>
                <PrecisionManufacturingOutlined />
              </ListItemIcon>
              <ListItemText
                primary="3D Printing Service"
                secondary="Instant automated quotes"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.92rem' }}
                secondaryTypographyProps={{ fontSize: '0.72rem' }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/services/3d-design-prototyping'}
              onClick={() => { navigate('/services/3d-design-prototyping'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.accent.coral }}>
                <DesignServicesOutlined />
              </ListItemIcon>
              <ListItemText
                primary="3D Design & Prototyping"
                secondary="CAD modeling & rapid testing"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.92rem' }}
                secondaryTypographyProps={{ fontSize: '0.72rem' }}
              />
            </ListItemButton>
          </ListItem>

          {/* More Links */}
          <Box sx={{ my: 1.5, px: 1 }}>
            <Typography variant="caption" sx={{ color: COLORS.text.secondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}>
              Information
            </Typography>
          </Box>

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/about'}
              onClick={() => { navigate('/about'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.text.secondary }}>
                <InfoOutlined />
              </ListItemIcon>
              <ListItemText primary="About Us" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/faq'}
              onClick={() => { navigate('/faq'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.text.secondary }}>
                <HelpOutlineOutlined />
              </ListItemIcon>
              <ListItemText primary="FAQ" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === '/contact'}
              onClick={() => { navigate('/contact'); setMobileDrawerOpen(false); }}
              sx={{
                borderRadius: '14px',
                py: 1.2,
                '&.Mui-selected': {
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontWeight: 700,
                  '& .MuiListItemIcon-root': { color: COLORS.primary.main },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: COLORS.text.secondary }}>
                <AlternateEmailOutlined />
              </ListItemIcon>
              <ListItemText primary="Contact Lab" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      {/* Drawer Footer Account Section */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
          bgcolor: COLORS.surface.containerLow,
        }}
      >
        {user ? (
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Avatar
                sx={{
                  bgcolor: COLORS.primary.main,
                  color: COLORS.primary.contrastText,
                  width: 38,
                  height: 38,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                }}
              >
                {(userData?.displayName || user.email || 'U')[0].toUpperCase()}
              </Avatar>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                  {userData?.displayName || user.displayName || 'Maker Account'}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: COLORS.text.secondary, display: 'block' }}>
                  {user.email}
                </Typography>
              </Box>
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mb={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ReceiptLongOutlined />}
                onClick={() => { navigate('/orders'); setMobileDrawerOpen(false); }}
                sx={{ fontSize: '0.75rem', py: 0.7 }}
              >
                Orders
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RequestQuoteOutlined />}
                onClick={() => { navigate('/my-quotes'); setMobileDrawerOpen(false); }}
                sx={{ fontSize: '0.75rem', py: 0.7 }}
              >
                Quotes
              </Button>
            </Box>
            {isAdmin() && (
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<AdminPanelSettingsOutlined />}
                onClick={() => { navigate('/admin'); setMobileDrawerOpen(false); }}
                sx={{ mb: 1, fontSize: '0.75rem' }}
              >
                Admin Dashboard
              </Button>
            )}
            <Button
              fullWidth
              color="error"
              size="small"
              startIcon={<LogoutOutlined />}
              onClick={() => { handleLogout(); setMobileDrawerOpen(false); }}
              sx={{ fontSize: '0.78rem' }}
            >
              Sign Out
            </Button>
          </Box>
        ) : (
          <Box display="flex" gap={1}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              startIcon={<LoginOutlined />}
              onClick={() => { navigate('/login'); setMobileDrawerOpen(false); }}
              sx={{ fontWeight: 700, fontSize: '0.85rem' }}
            >
              Login
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<PersonAddOutlined />}
              onClick={() => { navigate('/register'); setMobileDrawerOpen(false); }}
              sx={{ fontWeight: 700, fontSize: '0.85rem' }}
            >
              Register
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );

  return (
    <>
      {/* Funky multicolor accent strip */}
      <Box
        sx={{
          height: 3,
          width: '100%',
          background: `linear-gradient(90deg, ${COLORS.primary.main} 0%, ${COLORS.accent.lemon} 28%, ${COLORS.accent.coral} 52%, ${COLORS.accent.sky} 76%, ${COLORS.accent.grape} 100%)`,
        }}
      />

      {/* Glassmorphic Sticky Navbar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          color: COLORS.text.primary,
          borderBottom: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ px: { xs: 0, sm: 1 }, minHeight: { xs: 64, md: 72 }, display: 'flex', alignItems: 'center' }}>
            
            {/* Brand Logo & Funky Maker Badge */}
            <Box
              display="flex"
              alignItems="center"
              gap={1.25}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => navigate('/')}
            >
              <img
                src="/images/The-Random-Store---Logo---Transparent - Copy.png"
                alt="The Random Store Logo"
                style={{
                  height: 44,
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Box
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.2,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: withOpacity(COLORS.primary.main, 0.12),
                  color: COLORS.primary.dark,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                }}
              >
                <TuneOutlined sx={{ fontSize: 13, color: COLORS.primary.main }} />
                3D Print Lab
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation Links */}
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={0.5} sx={{ mr: 2 }}>
                {menuItems.map((item) => {
                  if (item.hasDropdown) {
                    return (
                      <Box key={item.label}>
                        <Button
                          onClick={handleServicesMenuOpen}
                          endIcon={<KeyboardArrowDown sx={{ fontSize: '1rem !important', transition: 'transform 0.2s', transform: Boolean(servicesMenuAnchor) ? 'rotate(180deg)' : 'none' }} />}
                          sx={{
                            color: isServicesActive ? COLORS.primary.dark : COLORS.text.primary,
                            bgcolor: isServicesActive ? withOpacity(COLORS.primary.main, 0.12) : 'transparent',
                            fontWeight: isServicesActive ? 700 : 600,
                            fontSize: '0.9rem',
                            px: 1.75,
                            py: 0.8,
                            borderRadius: '999px',
                            textTransform: 'none',
                            '&:hover': {
                              bgcolor: isServicesActive ? withOpacity(COLORS.primary.main, 0.18) : 'rgba(0,0,0,0.04)',
                            },
                          }}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          anchorEl={servicesMenuAnchor}
                          open={Boolean(servicesMenuAnchor)}
                          onClose={handleServicesMenuClose}
                          MenuListProps={{
                            sx: { p: 1 },
                          }}
                          PaperProps={{
                            sx: {
                              borderRadius: '20px',
                              boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
                              border: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
                              mt: 1,
                              minWidth: 260,
                            },
                          }}
                          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                        >
                          {servicesMenuItems.map((serviceItem) => (
                            <MenuItem
                              key={serviceItem.label}
                              onClick={() => {
                                navigate(serviceItem.path);
                                handleServicesMenuClose();
                              }}
                              sx={{
                                py: 1.25,
                                px: 1.5,
                                borderRadius: '12px',
                                mb: 0.5,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: withOpacity(COLORS.primary.main, 0.08),
                                  transform: 'translateX(3px)',
                                },
                              }}
                            >
                              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                {serviceItem.icon}
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.text.primary, lineHeight: 1.2 }}>
                                  {serviceItem.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: COLORS.text.secondary, fontSize: '0.72rem' }}>
                                  {serviceItem.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    );
                  }

                  const isActive = location.pathname === item.path;
                  return (
                    <Button
                      key={item.label}
                      onClick={() => navigate(item.path)}
                      sx={{
                        color: isActive ? COLORS.primary.dark : COLORS.text.primary,
                        bgcolor: isActive ? withOpacity(COLORS.primary.main, 0.12) : 'transparent',
                        fontWeight: isActive ? 700 : 600,
                        fontSize: '0.9rem',
                        px: 1.75,
                        py: 0.8,
                        borderRadius: '999px',
                        textTransform: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: isActive ? withOpacity(COLORS.primary.main, 0.18) : 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Right Action Cluster */}
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
              {/* Instant 3D Quote CTA (Desktop) */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<CalculateOutlined />}
                onClick={() => navigate('/services/3d-printing')}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  borderRadius: 999,
                  px: 2.2,
                  py: 0.8,
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(74, 191, 24, 0.28)',
                  whiteSpace: 'nowrap',
                }}
              >
                3D Quote
              </Button>

              {/* Shopping Cart Button */}
              <IconButton
                onClick={() => navigate('/cart')}
                aria-label={`Shopping cart with ${cartCount} items`}
                sx={{
                  color: COLORS.text.primary,
                  bgcolor: cartCount > 0 ? withOpacity(COLORS.primary.main, 0.12) : COLORS.surface.container,
                  borderRadius: '14px',
                  width: 44,
                  height: 44,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: withOpacity(COLORS.primary.main, 0.22),
                    transform: 'scale(1.06)',
                  },
                }}
              >
                <Badge
                  badgeContent={cartCount}
                  className={cartCount > 0 ? 'cartBadgeBounce' : ''}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: COLORS.primary.main,
                      color: COLORS.primary.contrastText,
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      height: 18,
                      minWidth: 18,
                      borderRadius: 999,
                    },
                  }}
                >
                  <ShoppingCartOutlined fontSize="small" />
                </Badge>
              </IconButton>

              {/* User Account / Profile */}
              {user ? (
                <>
                  <IconButton
                    onClick={handleUserMenuOpen}
                    aria-label="User account menu"
                    sx={{
                      p: 0.5,
                      borderRadius: '14px',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { transform: 'scale(1.06)' },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: COLORS.primary.main,
                        color: COLORS.primary.contrastText,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                      }}
                    >
                      {(userData?.displayName || user.email || 'U')[0].toUpperCase()}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    MenuListProps={{ sx: { p: 1 } }}
                    PaperProps={{
                      sx: {
                        borderRadius: '20px',
                        boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
                        border: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`,
                        mt: 1,
                        minWidth: 220,
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${COLORS.outline?.variant || 'rgba(0, 0, 0, 0.08)'}`, mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {userData?.displayName || 'Maker Account'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.text.secondary }}>
                        {user.email}
                      </Typography>
                    </Box>
                    <MenuItem
                      onClick={() => { navigate('/profile'); handleUserMenuClose(); }}
                      sx={{ borderRadius: '10px', py: 1, px: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: COLORS.text.secondary }}>
                        <AccountCircleOutlined fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>My Profile</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => { navigate('/orders'); handleUserMenuClose(); }}
                      sx={{ borderRadius: '10px', py: 1, px: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: COLORS.text.secondary }}>
                        <ReceiptLongOutlined fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>My Orders</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => { navigate('/my-quotes'); handleUserMenuClose(); }}
                      sx={{ borderRadius: '10px', py: 1, px: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: COLORS.text.secondary }}>
                        <RequestQuoteOutlined fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>My 3D Quotes</Typography>
                    </MenuItem>
                    {isAdmin() && (
                      <MenuItem
                        onClick={() => { navigate('/admin'); handleUserMenuClose(); }}
                        sx={{ borderRadius: '10px', py: 1, px: 1.5, color: COLORS.accent.coral }}
                      >
                        <ListItemIcon sx={{ minWidth: 34, color: COLORS.accent.coral }}>
                          <AdminPanelSettingsOutlined fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Admin Lab</Typography>
                      </MenuItem>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      onClick={handleLogout}
                      sx={{ borderRadius: '10px', py: 1, px: 1.5, color: COLORS.error.main }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: COLORS.error.main }}>
                        <LogoutOutlined fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Log Out</Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => navigate('/login')}
                    sx={{ fontWeight: 700, fontSize: '0.85rem', px: 1.75, borderRadius: 999 }}
                  >
                    Log In
                  </Button>
                </Box>
              )}

              {/* Hamburger Button for Mobile */}
              {isMobile && (
                <IconButton
                  onClick={() => setMobileDrawerOpen(true)}
                  aria-label="Open mobile navigation menu"
                  sx={{
                    color: COLORS.text.primary,
                    bgcolor: COLORS.surface.container,
                    borderRadius: '14px',
                    width: 44,
                    height: 44,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: COLORS.surface.containerHigh,
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Render Mobile Navigation Sheet */}
      <MobileDrawer />
    </>
  );
};

export default Navbar;
